/**
 * 曲线数学计算模块
 * 封装各种曲线计算逻辑
 */

/**
 * 根据角度获取方向值
 * @param {string} directionStr - 方向字符串，"1"表示顺时针，"-1"表示逆时针
 * @returns {number} 方向值，1表示顺时针，-1表示逆时针
 */
export const getDirectionValue = (directionStr) => {
  return directionStr === '1' ? 1 : -1
}

/**
 * 将角度转换为弧度
 * @param {number} degrees - 角度值
 * @returns {number} 弧度值
 */
export const degreesToRadians = (degrees) => {
  return degrees * Math.PI / 180
}

/**
 * 计算贝塞尔曲线控制点长度
 * @param {number} radius - 圆半径
 * @param {number} angle - 圆心角(弧度)
 * @returns {number} 控制点长度
 */
export const calculateControlPointLength = (radius, angle) => {
  // 4/3*(1-cosθ/2)/sinθ/2 是计算贝塞尔曲线控制点的经验公式
  return radius * (4/3) * ((1 - Math.cos(angle/2)) / Math.sin(angle/2))
}

/**
 * 计算变径曲线的基本参数
 * @param {object} params - 输入参数
 * @param {number} params.diameter - 本体直径
 * @param {number} params.theta - 对应角度
 * @param {number} params.width0 - 跑道宽度
 * @param {number} params.width - 变径尺寸
 * @param {number} params.w_theta - 跑道倾角
 * @param {number} params.decrease - 下降角度
 * @param {string} params.thDirect - 方向
 * @returns {object} 计算结果
 */
export const calculateVarietyCurveParams = (params) => {
  const { diameter, theta, width0, width, w_theta, decrease, thDirect } = params
  
  // 计算基础参数
  const direction = getDirectionValue(thDirect)
  const radius = diameter / 2
  const wThetaRad = degreesToRadians(w_theta)
  const decreaseRad = degreesToRadians(decrease)
  const thetaRad = direction * degreesToRadians(theta)
  const adjustedWidth0 = width0 * Math.cos(wThetaRad)
  
  // 计算关键点坐标
  const X1 = radius
  const Y1 = 0
  const Xa = radius + width
  const Ya = 0
  const X2 = (radius - adjustedWidth0) * Math.cos(thetaRad)
  const Y2 = (radius - adjustedWidth0) * Math.sin(thetaRad)
  const Xb = radius * Math.cos(thetaRad)
  const Yb = radius * Math.sin(thetaRad)
  
  // 计算圆心
  const C1x = (X2**2 + Y2**2 - X1**2 - Y1**2) / (2 * (X2 - X1))
  const C1y = 0
  const Cax = (Xb**2 + Yb**2 - Xa**2 - Ya**2) / (2 * (Xb - Xa))
  const Cay = 0
  
  // 计算距离
  const D_X1 = X2 - C1x
  const D_Y1 = Y2 - C1y
  const D_Xa = Xb - Cax
  const D_Ya = Yb - Cay
  
  // 计算贝塞尔曲线控制点长度
  const La = calculateControlPointLength(radius + width, thetaRad)
  const Lb = calculateControlPointLength(radius, thetaRad)
  const L1 = calculateControlPointLength(radius, thetaRad)
  const L2 = calculateControlPointLength(radius - adjustedWidth0, thetaRad)
  
  // 计算贝塞尔曲线控制点
  const Bxa = radius + width
  const Bya = La
  const Bxb = Lb * Math.cos(thetaRad - Math.PI/2) + Xb
  const Byb = Lb * Math.sin(thetaRad - Math.PI/2) + Yb
  const Bx1 = radius
  const By1 = L1
  const Bx2 = L2 * Math.cos(thetaRad - Math.PI/2) + X2
  const By2 = L2 * Math.sin(thetaRad - Math.PI/2) + Y2
  
  // 计算角度
  let t1, ta
  if (D_X1 === 0) {
    t1 = Math.PI/2
  } else if (D_X1 > 0) {
    t1 = Math.atan(D_Y1 / D_X1)
  } else {
    t1 = Math.atan(D_Y1 / D_X1) - Math.PI
  }
  
  if (D_Xa === 0) {
    ta = Math.PI/2
  } else if (D_Xa > 0) {
    ta = Math.atan(D_Ya / D_Xa)
  } else {
    ta = Math.atan(D_Ya / D_Xa) - Math.PI
  }
  
  return {
    direction,
    radius,
    thetaRad,
    wThetaRad,
    decreaseRad,
    adjustedWidth0,
    X1, Y1, Xa, Ya, X2, Y2, Xb, Yb,
    C1x, C1y, Cax, Cay,
    D_X1, D_Y1, D_Xa, D_Ya,
    La, Lb, L1, L2,
    Bxa, Bya, Bxb, Byb, Bx1, By1, Bx2, By2,
    t1, ta
  }
}

/**
 * 计算曲线点的z坐标
 * @param {object} params - 输入参数
 * @param {number} params.points1 - 第一条曲线点数组
 * @param {number} params.points2 - 第二条曲线点数组
 * @param {number} params.radius - 圆半径
 * @param {number} params.decreaseRad - 下降角度(弧度)
 * @param {number} params.wThetaRad - 跑道倾角(弧度)
 * @param {number} params.n - 线段数
 * @returns {object} 更新后的点数组
 */
export const calculatePointsZ = (params) => {
  const { points1, points2, radius, decreaseRad, wThetaRad, n } = params
  const t_solve = new Array(n + 1)
  const t1_solve = new Array(n + 1)
  const t2_solve = new Array(n + 1)
  const pointsX_circle = new Array(n + 1)
  const pointsY_circle = new Array(n + 1)
  const L_1C = new Array(n + 1)
  const L_2C = new Array(n + 1)
  const theta_1 = new Array(n + 1)
  
  for (let i = 0; i <= n; i++) {
    // 求解二次方程
    const a = points1[i].x**2 - 2 * points1[i].x * points2[i].x + points2[i].x**2 + 
              points1[i].y**2 - 2 * points1[i].y * points2[i].y + points2[i].y**2
    const b = -(2 * points1[i].x * points2[i].x - 2 * points2[i].x**2 + 
               2 * points1[i].y * points2[i].y - 2 * points2[i].y**2)
    const c = points2[i].x**2 + points2[i].y**2 - radius**2
    
    // 计算判别式
    const discriminant = b**2 - 4 * a * c
    
    // 求解根
    if (discriminant < 0) {
      t1_solve[i] = 0
      t2_solve[i] = 0
    } else {
      t1_solve[i] = (-b + Math.sqrt(discriminant)) / (2 * a)
      t2_solve[i] = (-b - Math.sqrt(discriminant)) / (2 * a)
    }
    
    // 选择合适的解
    if (t2_solve[i] >= -0.1 && t2_solve[i] <= 1.1) {
      t_solve[i] = t2_solve[i]
    } else {
      t_solve[i] = t1_solve[i]
    }
    
    // 计算圆心点
    pointsX_circle[i] = points1[i].x * t_solve[i] + points2[i].x * (1 - t_solve[i])
    pointsY_circle[i] = points1[i].y * t_solve[i] + points2[i].y * (1 - t_solve[i])
    
    // 计算距离
    L_1C[i] = Math.sqrt(
      (points1[i].x - pointsX_circle[i])**2 + (points1[i].y - pointsY_circle[i])**2
    )
    L_2C[i] = Math.sqrt(
      (points2[i].x - pointsX_circle[i])**2 + (points2[i].y - pointsY_circle[i])**2
    )
    
    // 计算角度插值
    theta_1[i] = decreaseRad + ((wThetaRad - decreaseRad) * (i / n))
    
    // 计算z坐标
    points1[i].z = L_1C[i] * Math.tan(theta_1[i])
    points2[i].z = -L_2C[i] * Math.tan(theta_1[i])
  }
  
  return { points1, points2 }
}

/**
 * 获取优化的线段数
 * 根据设备性能动态调整线段数
 * @returns {number} 线段数
 */
export const getOptimalSegments = () => {
  // 根据设备性能调整线段数
  if (navigator.hardwareConcurrency > 4) {
    return 400
  } else {
    return 200
  }
}

/**
 * 生成当前日期字符串
 * @returns {string} 日期字符串，格式：YYYYMMDD
 */
export const getCurrentDateString = () => {
  const date = new Date()
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}${month}${day}`
}